import devHours from "../../components/industries/sections/devHours"

/**
 * /industries/manufacturing data — rebuilt from
 * src/Design/industries/industry-manufacturing.html, word for word, PLUS
 * the "Wherever you're starting from" situations section (replacing the
 * old single-offer solution section) and the hero/stats/why/FAQ changes
 * from the "lead with problems, not one offer" ticket.
 *
 * Situations section links verified against src/data/offers.js directly
 * (not just this file's own prior claims):
 * - Odoo for manufacturers (odoo-for-manufacturers): included covers MRP/
 *   BOMs/routings, purchasing and inventory; "five weeks" for the core
 *   system is the offer's own metaTitle/metaDescription/timeline, not just
 *   this page's claim.
 * - Dynamics 365 support (dynamics-365-support): "named engineer" and
 *   "160 hours a month" are both real `included` fields; the offer's own
 *   note confirms coverage is business hours, not 24/7.
 * - ERP Rescue Sprint (erp-rescue-sprint): exists as its own offer.
 * - AI Quote & Document Automation (ai-quote-and-document-automation):
 *   `included` covers both quotes and documents (contracts/SOWs), matching
 *   "Document automation" below.
 *
 * Title tag and meta description are unchanged from the previous version of
 * this page.
 */
const manufacturing = ({ heroImage, challengeImages = [], solutionImage, resultsImage }) => ({
  slug: "manufacturing",
  industryName: "Manufacturing",

  kicker: "Solutions for manufacturers who've outgrown spreadsheets",
  headline: "Know what every job costs before it ships",
  subheading:
    "Whether you're starting fresh, stuck with an ERP that never fitted, or need something built around your process — we connect BOMs, purchasing, production and shipping so your costs add up.",
  heroImage,
  heroImageAlt: "Manufacturing work in progress",
  ctaHref: "/contact",
  ctaLabel: "Book your free consultation",
  badge: "Cakes & Bakes · Dynamics 365 re-implemented",
  // Proof stats only — no offer-specific weeks/hours/prices here (those
  // live in the situations cards and the offer pages themselves).
  stats: [
    { value: "15+", label: "Odoo implementations" },
    { value: "35+", label: "Solutions delivered" },
    { value: `${devHours()}`, label: "Development hours" },
    { value: "30 days", label: "Post-launch warranty" },
  ],

  challengesEyebrow: "What slows manufacturing teams down",
  challengesHeading: "Four places margin quietly leaks",
  challengesIntro: "Each one costs time on the floor and money on the invoice.",
  challenges: [
    {
      image: challengeImages[0],
      heading: "BOMs in spreadsheets",
      detail: "Nobody is sure which version is current, and one wrong BOM means rework.",
    },
    {
      image: challengeImages[1],
      heading: "No batch traceability",
      detail: "When a customer calls, you can't say which batch went into which shipment.",
    },
    {
      image: challengeImages[2],
      heading: "Quotes take days",
      detail: "Costing is worked out by hand, so every quote waits on one person.",
    },
    {
      image: challengeImages[3],
      heading: "Margins found too late",
      detail: "You learn a job lost money only after it has shipped.",
    },
  ],

  situationsEyebrow: "How we help manufacturing teams",
  situationsHeading: "Wherever you're starting from",
  situationsIntro: "Every manufacturer arrives with a different setup. These are the four we see most, and how we handle each.",
  situations: [
    {
      tag: "Starting fresh",
      title: "Outgrowing spreadsheets",
      detail: "We set up Odoo around your production, BOMs, purchasing and stock, with the core running in five weeks.",
      ghostIconPath:
        '<path d="M5 15c-1 1-1.5 3.5-1.5 5.5 2 0 4.5-.5 5.5-1.5"/><path d="M9 15l-3-3c1.5-4 5-8 12-8 0 7-4 10.5-8 12z"/><circle cx="14.5" cy="9.5" r="1.5"/>',
      footerText: "Odoo for manufacturers",
      footerHref: "/offers/odoo-for-manufacturers",
    },
    {
      tag: "Already on Dynamics 365",
      title: "Keep the system you have",
      detail: "A named engineer who knows your setup maintains and extends it, instead of replacing it.",
      ghostIconPath: '<path d="M20 11a8 8 0 0 0-14.8-3.5M4 4v4h4"/><path d="M4 13a8 8 0 0 0 14.8 3.5M20 20v-4h-4"/>',
      footerText: "Dynamics 365 support",
      footerHref: "/offers/dynamics-365-support",
    },
    {
      tag: "ERP not working",
      title: "Stuck with an ERP that never fitted",
      detail: "We find what went wrong and get it working before you spend money on a replacement.",
      ghostIconPath: '<path d="M7 10h3V7L6.5 3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1-3 3l-6-6a6 6 0 0 1-8-8L7 10"/>',
      footerText: "ERP Rescue Sprint",
      footerHref: "/offers/erp-rescue-sprint",
    },
    {
      tag: "Manual paperwork",
      title: "Quotes and documents by hand",
      detail: "We automate quotes and documents from your real costs, so they go out faster and match the job.",
      ghostIconPath: '<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6"/>',
      footerText: "Document automation",
      footerHref: "/offers/ai-quote-and-document-automation",
    },
  ],
  situationsUnsureHeading: "Not sure which fits?",
  situationsUnsureDetail: "The Fit Sprint gives you a costed plan before you commit to anything.",
  situationsUnsureLinkLabel: "See the Fit Sprint",
  situationsUnsureLinkHref: "/offers/ops-and-erp-fit-sprint",

  resultsEyebrow: "Results for manufacturing clients",
  resultsHeading: "From paper registers to one Dynamics 365 system",
  resultsMetrics: [
    { value: "100%", label: "of issuance and dispatch recorded in Dynamics 365" },
    { value: "Live", label: "manufacturing running in Dynamics 365 for the first time" },
  ],
  resultsSummary: "Production, stock and dispatch now come from one system instead of registers and spreadsheets.",
  resultsLinkLabel: "Read the case study",
  resultsLinkHref: "/caseStudies/projects/cakesAndBakes",
  resultsImage,
  resultsImageAlt: "Cakes & Bakes",

  // Names resolved against src/data/clientLogos.js by TrustedBy.js.
  trustedByLogos: ["Cakes & Bakes", "EcoZohm", "United Gulf Pipe Manufacturing", "Wall's"],

  whyEyebrow: "Why Visiomate for manufacturing",
  whyHeading: "What you get with this team",
  whyIntro: "Three things that stay the same on every engagement.",
  whyLinkHref: "/contact",
  reasons: [
    {
      title: "Same team, start to finish",
      detail: "The people who start your project finish it, with the founder involved throughout.",
    },
    { title: "30-day warranty", detail: "Everything we configure or build is covered for 30 days after go-live." },
    {
      title: "We pick what fits your setup",
      detail: "Odoo, Dynamics 365 or a custom build — we recommend what suits your business, not one product.",
    },
  ],

  questionsEyebrow: "Questions",
  questionsHeading: "Questions buyers ask before they commit",
  questionsIntro: "Something else on your mind?",
  questionsLinkHref: "/contact",
  faqs: [
    {
      q: "We're a small manufacturer — is a full ERP overkill for us?",
      a: "Not usually. We start with what you need now, often production, BOMs, purchasing and stock, and add the rest later.",
    },
    {
      q: "We already run Dynamics 365 — do we have to replace it?",
      a: "No. We can maintain and extend the system you have, and only suggest a move if it's genuinely holding you back.",
    },
    {
      q: "Can you connect it to the systems we already use?",
      a: "Yes. We connect your ERP to e-commerce, accounting, shipping and other tools, so information is entered once.",
    },
    {
      q: "How do we start if we're not sure what we need?",
      a: "With the Fit Sprint: we map how you work and give you a costed plan before you commit to anything.",
    },
  ],

  ctaHeading: "Start with a costed roadmap, not a proposal",
  ctaDetail: "The Ops & ERP Fit Sprint gives you a costed plan before you commit to anything larger.",
  ctaLinkLabel: "See the Fit Sprint",
  ctaLinkHref: "/offers/ops-and-erp-fit-sprint",

  metaTitle: "Manufacturing Software Development Company | Visiomate",
  metaDescription:
    "Fixed-scope Odoo and Dynamics 365 work for manufacturers: BOMs, batch traceability, job costing and shipping automation. Live in five to eight weeks.",
})

export default manufacturing
